import * as React from 'react';
import { cva } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';

import { Icon } from '@/components/ui/Icon';
import { resolveButtonToneAppearanceClassName } from './buttonAppearance';
import { resolveGroupedCornerClassName, useButtonGroupContext } from './ButtonGroup';
import { cn } from './cn';

// Button — labelled action primitive. Always renders a text label (with an
// optional leading/trailing icon). For icon-only hits (close X, inline
// chrome actions, toolbar buttons), use `<IconButton>` instead.
//
// Visual contract is two orthogonal axes:
//   - tone:        primary | secondary | destructive | info | neutral
//   - appearance:  solid | outline | ghost
//
// See ./buttonAppearance.js for the supported (tone, appearance) matrix.
// `(neutral, solid)` is intentionally absent — high emphasis without
// semantic weight is a contradiction. Asking for it throws.

const BUTTON_BASE_CLASSNAME = 'tw-relative tw-inline-flex tw-select-none tw-items-center tw-gap-2 tw-whitespace-nowrap tw-text-sm tw-font-semibold tw-shadow-none tw-ring-offset-background tw-transition-colors focus:tw-outline-none focus-visible:tw-outline-none focus-visible:tw-ring-0 focus-visible:tw-ring-offset-0 disabled:tw-pointer-events-none disabled:tw-opacity-50 [&_svg]:tw-pointer-events-none [&_svg]:tw-size-4 [&_svg]:tw-shrink-0';

// Canonical control-height scale shared with IconButton / Select /
// Input / TextArea: small=32, medium=40 (default), large=44. Earlier
// drafts kept a 36px "small" and a 32px "x-small" — both dropped so
// the scale is consistent across primitives.
const buttonSizeVariants = cva(BUTTON_BASE_CLASSNAME, {
    variants: {
        size: {
            small: 'tw-h-8 tw-rounded-lg tw-px-2.5 tw-text-xs',
            medium: 'tw-h-10 tw-rounded-lg tw-px-4 tw-py-2',
            large: 'tw-h-11 tw-rounded-lg tw-px-8'
        }
    },
    defaultVariants: {
        size: 'medium'
    }
});

// Loader inherits text colour via `tw-border-current` so it contrasts
// correctly in every (tone, appearance) cell — solid cells paint a
// foreground-toned spinner, outline / ghost cells paint a tone-coloured
// one. Positioning is handled by an absolute wrapper around the spinner
// (see the render below), so this class stays purely visual.
const LOADER_CLASSNAME = 'tw-w-4 tw-h-4 tw-border-4 tw-border-current tw-border-t-transparent tw-rounded-full tw-animate-spin tw-opacity-70';

// Button forces every nested SVG to 16x16 via `[&_svg]:tw-size-4` in the cva
// base, so the rendered icon size is identical regardless of button size.
// Strings are resolved through Icon (which still needs *some* size hint to
// pick the right CSS class — `small` keeps the leading-none geometry tight).
const renderIconSlot = (icon) => {
    if (!icon) {
        return null;
    }

    if (typeof icon === 'string') {
        return <Icon icon={icon} size="small" tone="inherit" />;
    }

    return icon;
};

const Button = React.forwardRef(
    ({
        appearance = 'solid',
        asChild = false,
        children,
        className: _className,
        style: _style,
        disabled,
        fullWidth = false,
        icon,
        iconAlign = 'adjacent',
        iconPosition = 'leading',
        loading,
        size = 'medium',
        tone = 'primary',
        ...props
    }, ref) => {
        const groupContext = useButtonGroupContext();
        const groupedCornerClassName = resolveGroupedCornerClassName(groupContext);
        const toneAppearanceClassName = resolveButtonToneAppearanceClassName(tone, appearance);

        const iconNode = renderIconSlot(icon);
        const isEdgeAlign = iconAlign === 'edge';

        // Visible content lives inside a span that ALWAYS occupies its
        // natural width. During loading we toggle `tw-invisible` on the
        // span — layout is preserved, no offsetWidth measurement needed,
        // no race conditions on transitions. The spinner overlays the
        // button via an absolute wrapper that fills the button box.
        const visibleContent = (
            <span
                className={cn(
                    'tw-inline-flex tw-items-center tw-gap-2',
                    isEdgeAlign && 'tw-w-full tw-justify-between',
                    loading && 'tw-invisible'
                )}
            >
                {iconNode && iconPosition === 'leading' ? iconNode : null}
                {children}
                {iconNode && iconPosition === 'trailing' ? iconNode : null}
            </span>
        );

        const buttonClassName = cn(
            buttonSizeVariants({ size }),
            toneAppearanceClassName,
            isEdgeAlign ? 'tw-justify-between' : 'tw-justify-center',
            fullWidth && 'tw-w-full',
            groupedCornerClassName
        );

        // `asChild` is the escape hatch for "use my element with Button's
        // styling" — typically `<Button asChild><a>…</a></Button>` to
        // render a link styled as a button. Radix's Slot expects exactly
        // one React element child to clone props onto, so we MUST pass
        // `children` directly here. The visibleContent + loading wrapper
        // used by the native path would hand Slot two children (one
        // null) and trip `React.Children.only`. Trade-off: in asChild
        // mode, `icon` and `loading` are ignored — the consumer's
        // element owns its content. For the rare case where icon /
        // loading matter alongside a custom element, drop `asChild` and
        // wrap explicitly.
        if (asChild) {
            return (
                <Slot
                    className={buttonClassName}
                    ref={ref}
                    {...props}
                >
                    {children}
                </Slot>
            );
        }

        // Default to `type="button"` so a Button rendered inside a <form>
        // doesn't act as a submit button by accident. Native HTML defaults
        // a bare <button> inside a form to `type="submit"`, which silently
        // triggers form submission on every click — a footgun every React
        // app eventually hits. Callers that genuinely want a submit button
        // pass `type="submit"` explicitly. `{...props}` is spread AFTER the
        // default so consumer-provided `type` wins.
        return (
            <button
                type="button"
                className={buttonClassName}
                disabled={loading || disabled}
                ref={ref}
                {...props}
            >
                {visibleContent}
                {loading ? (
                    <span aria-hidden="true" className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
                        <svg className={LOADER_CLASSNAME} viewBox="0 0 24 24" />
                    </span>
                ) : null}
            </button>
        );
    }
);
Button.displayName = 'Button';

export { Button };
